import { supabase } from "../supabase/SupabaseClient";
import { createRow } from "./supabaseRealtime";
import { actual } from "./quien";

/* Infracciones y sanciones (#118, pedido de Erick).
 *
 * DOS TABLAS, DOS HECHOS: la infracción es lo que pasó en la ruta; la sanción
 * —apercibimiento o suspensión— es una decisión que firma una persona. La
 * regla de cuándo corresponde vive en `shared/infracciones`, no acá ni en la
 * pantalla: el aviso del bot cuenta con la misma regla, y dos copias un día
 * cuentan distinto.
 *
 * Mismas llaves que licencias y nómina (`ver.rrhh` / `cargar.rrhh`): es el
 * mismo tipo de dato. La pared es la RLS, la pantalla sólo lo cuenta.
 *
 * ANULAR NO BORRA, en ninguna de las dos: la fila queda con quién y cuándo.
 * Una sanción anulada además REACTIVA el contador —las infracciones que
 * había tapado vuelven a contar— y eso no lo hace nadie a mano: es la regla
 * leyendo los datos como quedaron.
 */

/* `episodio` viaja con la fila: es el hecho congelado —tractor, puntas,
 * velocidades, carga— del que sale el texto del documento. No se muestra
 * crudo en la pantalla; lo que habilita es el link al papel. Sin episodio no
 * hay documento que armar, y por eso la lista tiene que saber si está. */
const CAMPOS_INF =
  "id,dni,chofer_nombre,fecha,detalle,origen,origen_ref,episodio,descargo," +
  "descargo_en,aviso_4ta_en,usuario,usuario_nombre,creado_en,anulado_en," +
  "anulado_por";

const CAMPOS_SAN =
  "id,dni,chofer_nombre,tipo,fecha,nota,usuario,usuario_nombre," +
  "creado_en,anulado_en,anulado_por";

/* Las dos tablas de los últimos dos años, para la pantalla. Dos años y no
 * uno: la ventana del contador es de 12 meses, y para explicar «por qué no
 * cuenta» hace falta ver también lo que quedó justo afuera.
 *
 * El tope de 999 es el techo REAL de una página de PostgREST (ver
 * `sinLimitesFalsos.test`): un `.limit(2000)` prometería «todas» y traería
 * mil calladas. Con más de 999 infracciones en dos años habrá que paginar —
 * hoy la tabla nace vacía. */
export async function traerInfracciones() {
  const corte = new Date(Date.now() - 2 * 365 * 24 * 3600 * 1000)
    .toISOString()
    .slice(0, 10);
  const [inf, san] = await Promise.all([
    supabase
      .from("infracciones")
      .select(CAMPOS_INF)
      .gte("fecha", corte)
      .order("fecha", { ascending: false })
      .limit(999),
    supabase
      .from("sanciones")
      .select(CAMPOS_SAN)
      .gte("fecha", corte)
      .order("fecha", { ascending: false })
      .limit(500),
  ]);
  if (inf.error) throw inf.error;
  if (san.error) throw san.error;
  return { infracciones: inf.data || [], sanciones: san.data || [] };
}

/* Por `createRow`, que sella quién cargó: una infracción anónima es la
 * primera que se discute. */
export const crearInfraccion = ({ dni, chofer_nombre, fecha, detalle, origen }) =>
  createRow("infracciones", null, {
    dni: String(dni).replace(/\D/g, ""),
    chofer_nombre: (chofer_nombre || "").trim() || null,
    fecha,
    detalle: (detalle || "").trim(),
    origen: (origen || "").trim() || null,
  });

/* La sanción la registra el clic de una persona con `cargar.rrhh` — ésa es
 * la firma que el ticket pide. El sistema nunca llama esto solo. */
export const crearSancion = ({ dni, chofer_nombre, tipo, fecha, nota }) =>
  createRow("sanciones", null, {
    dni: String(dni).replace(/\D/g, ""),
    chofer_nombre: (chofer_nombre || "").trim() || null,
    tipo,
    fecha,
    nota: (nota || "").trim() || null,
  });

/* Como `anularLicencia`: no pasa por `updateRow` para que el anulador no
 * pise a quién la cargó. */
async function anular(tabla, id) {
  const quien = actual();
  const { data, error } = await supabase
    .from(tabla)
    .update({
      anulado_en: new Date().toISOString(),
      anulado_por: quien?.mail || null,
    })
    .eq("id", id)
    .is("anulado_en", null)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export const anularInfraccion = (id) => anular("infracciones", id);
export const anularSancion = (id) => anular("sanciones", id);

/* Los días en que un tractor pasó los 800 km (pedido de Nico, 01/09, para
 * Erick Aguirre y Gustavo Lynch). Es la vista `km_dia_excedido`: km del GPS
 * día por día, con el chofer del viaje que cubre esa fecha cuando se puede
 * resolver. La pared es la misma de esta pantalla — sin `ver.rrhh` la vista
 * devuelve cero filas, no un error. */
export async function traerKmExcedidos() {
  const { data, error } = await supabase
    .from("km_dia_excedido")
    .select("fecha,interno,km,chofer")
    .order("fecha", { ascending: false })
    .order("km", { ascending: false })
    .limit(500);
  if (error) throw error;
  return data || [];
}
